<template>
	<div class="sidebar-link-list-container">
		<ul class="sidebar-link-list">
			<UiNavigationSidebarLink
				v-for="(item, index) in items"
				:key="item.id"
				:to="item.to"
				:icon="item.icon"
				:title="item.title"
				:active="item.to ? isActiveRoute(item.to) : false"
				:expanded="expanded"
				:mode="mode"
				:dragging="draggedIndex === index"
				:drag-over="dragOverIndex === index"
				@more="(event: MouseEvent) => emit('more', item.id, event)"
				@hide="() => emit('hide', item.id)"
				@reorder-mode="handleReorderMode"
				@dragstart="(event: DragEvent) => handleDragStart(index, event)"
				@dragend="handleDragEnd"
				@dragover="(event: DragEvent) => handleDragOver(index, event)"
				@dragleave="handleDragLeave"
				@drop="(event: DragEvent) => handleDrop(index, event)"
			/>
		</ul>
		<div class="edit-button-wrapper" v-if="items.length > 1">
			<button type="button" class="edit-button" :class="{ visible: expanded, editing: mode !== 'default' }" @click="handleButtonClick">{{ buttonText }}</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { SidebarLinkMode } from "./sidebarLink.vue";

export interface SidebarLinkItem {
	id: string;
	to?: string;
	icon?: string;
	title?: string;
}

interface Props {
	items: SidebarLinkItem[];
	expanded?: boolean;
}

withDefaults(defineProps<Props>(), {
	expanded: false,
});

const mode = defineModel<SidebarLinkMode>("mode", { default: "default" });

const emit = defineEmits<{
	more: [itemId: string, event: MouseEvent];
	hide: [itemId: string];
	reorder: [fromIndex: number, toIndex: number];
}>();

const buttonText = computed(() => {
	if (mode.value === "default") return "Edit";
	if (mode.value === "reorder") return "Save Reorder";
	return "Save Changes";
});

function handleButtonClick() {
	if (mode.value === "default") {
		mode.value = "edit";
	} else if (mode.value === "edit") {
		mode.value = "default";
	} else if (mode.value === "reorder") {
		mode.value = "edit";
	}
}

function handleReorderMode() {
	mode.value = "reorder";
}

const route = useRoute();

function isActiveRoute(routePath: string): boolean {
	return route.path === routePath;
}

// Drag and drop state
const draggedIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

function handleDragStart(index: number, event: DragEvent) {
	if (mode.value !== "reorder") return;
	draggedIndex.value = index;
	if (event.dataTransfer) {
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", String(index));
	}
}

function handleDragOver(index: number, _event: DragEvent) {
	if (mode.value !== "reorder") return;
	if (draggedIndex.value === null || draggedIndex.value === index) return;
	dragOverIndex.value = index;
}

function handleDragLeave() {
	dragOverIndex.value = null;
}

function handleDrop(index: number, _event: DragEvent) {
	if (mode.value !== "reorder") return;
	if (draggedIndex.value !== null && draggedIndex.value !== index) {
		emit("reorder", draggedIndex.value, index);
	}
	resetDragState();
}

function handleDragEnd() {
	resetDragState();
}

function resetDragState() {
	draggedIndex.value = null;
	dragOverIndex.value = null;
}

defineExpose({
	isActiveRoute,
});
</script>

<style lang="scss" scoped>
.sidebar-link-list-container {
	display: flex;
	flex-direction: column;
	width: 100%;
}

.sidebar-link-list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: 100%;
}

.edit-button-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin-top: 0.5rem;
}
.edit-button {
	opacity: 0;
	pointer-events: none;
	margin-top: 0.5rem;
	padding: 0.25rem 0.5rem;
	background: transparent;
	border: none;
	color: var(--text-700);
	width: 100px;
	font-size: 0.75rem;
	cursor: pointer;
	text-align: left;
	transition:
		opacity 0.2s ease,
		color 0.2s ease;
	display: flex;
	justify-content: center;

	&.visible {
		opacity: 1;
		pointer-events: auto;
	}

	&:hover {
		color: var(--text-300);
	}
}
</style>
